/**
 * src/admin-ui/src/pages/AdminUsers.tsx
 *
 * Administrator page for users and ball-balance operations, on the
 * shared full-screen table pattern (components/edge-table). The user
 * list is paginated on the server, so header filters are disabled and
 * search lives in the bottom bar; row click opens the balance dialog.
 * Tables inside the dialog reuse the shared EdgeTable look.
 */

import React, { FormEvent, useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Snackbar,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  AdminUser,
  AdminUserDetail,
  BalanceTransaction,
  adjustUserBalance,
  getAdminUser,
  getAdminUsers,
  getUserBalanceTransactions,
} from "../services/adminUsers";
import EdgeDataTable, {
  EdgeColumn,
} from "../components/edge-table/EdgeDataTable";
import {
  BarTile,
  EdgeTable,
} from "../components/edge-table/EdgeTableLayout.styles";
import { CenteredState } from "../styles/common";
import { FilterableCell } from "../utils/usageTableFilters";
import { formatBalls } from "../utils/format";

const TRANSACTION_LABELS: Record<BalanceTransaction["kind"], string> = {
  deposit: "Оплата",
  usage_charge: "Использование API",
  manual_adjust: "Ручная корректировка",
};

function formatDate(value: number | string): string {
  const date =
    typeof value === "number" ? new Date(value * 1000) : new Date(value);
  return date.toLocaleString("ru-RU");
}

function formatBallValue(value: string): string {
  return formatBalls(Number(value));
}

function transactionComment(transaction: BalanceTransaction): string {
  const reason = transaction.meta?.reason;
  if (typeof reason === "string") return reason;
  const modelName = transaction.meta?.model_name;
  if (typeof modelName === "string") return modelName;
  return "—";
}

const columns: EdgeColumn<AdminUser>[] = [
  { id: "user_id", label: "ID", numeric: true },
  { id: "name", label: "Пользователь" },
  { id: "email", label: "Email" },
  { id: "created_at", label: "Дата регистрации" },
];

const getCell = (row: AdminUser, columnId: string): FilterableCell => {
  switch (columnId) {
    case "user_id":
      return { num: row.user_id, text: String(row.user_id) };
    case "name":
      return { text: row.name || "—" };
    case "email":
      return { text: row.email || "—" };
    case "created_at":
      return { text: formatDate(row.created_at) };
    default:
      return { text: "—" };
  }
};

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [usersPage, setUsersPage] = useState(0);
  const [usersPageSize, setUsersPageSize] = useState(25);
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(true);

  const [selectedUser, setSelectedUser] = useState<AdminUserDetail | null>(
    null,
  );
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);
  const [transactions, setTransactions] = useState<BalanceTransaction[]>([]);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [transactionsPage, setTransactionsPage] = useState(0);
  const [transactionsPageSize, setTransactionsPageSize] = useState(25);

  const [deltaBalls, setDeltaBalls] = useState("");
  const [reason, setReason] = useState("");
  const [savingAdjustment, setSavingAdjustment] = useState(false);
  const [deductionConfirmationOpen, setDeductionConfirmationOpen] =
    useState(false);
  const [toast, setToast] = useState<{
    message: string;
    severity: "success" | "error";
  } | null>(null);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const response = await getAdminUsers(
        usersPage * usersPageSize,
        usersPageSize,
        appliedSearch,
      );
      setUsers(response.items);
      setTotalUsers(response.total);
    } catch (error) {
      console.error("Failed to load administrator user list", error);
      setToast({
        message: "Не удалось загрузить пользователей",
        severity: "error",
      });
    } finally {
      setLoadingUsers(false);
    }
  }, [appliedSearch, usersPage, usersPageSize]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const loadTransactions = useCallback(
    async (userId: number, page: number, pageSize: number) => {
      const response = await getUserBalanceTransactions(
        userId,
        page * pageSize,
        pageSize,
      );
      setTransactions(response.items);
      setTotalTransactions(response.total);
    },
    [],
  );

  const openUser = async (user: AdminUser) => {
    setUserDialogOpen(true);
    setLoadingUser(true);
    setTransactionsPage(0);
    setDeltaBalls("");
    setReason("");
    try {
      const [detail] = await Promise.all([
        getAdminUser(user.user_id),
        loadTransactions(user.user_id, 0, transactionsPageSize),
      ]);
      setSelectedUser(detail);
    } catch (error) {
      console.error("Failed to load administrator user detail", error);
      setToast({
        message: "Не удалось загрузить данные пользователя",
        severity: "error",
      });
      setUserDialogOpen(false);
    } finally {
      setLoadingUser(false);
    }
  };

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    setUsersPage(0);
    setAppliedSearch(searchInput.trim());
  };

  const validateAdjustment = (): number | null => {
    const amount = Number(deltaBalls);
    if (!Number.isFinite(amount) || amount === 0) {
      setToast({
        message: "Введите ненулевую сумму баллов",
        severity: "error",
      });
      return null;
    }
    if (!reason.trim()) {
      setToast({
        message: "Укажите комментарий к корректировке",
        severity: "error",
      });
      return null;
    }
    return amount;
  };

  const performAdjustment = async () => {
    if (!selectedUser) return;
    setSavingAdjustment(true);
    try {
      const response = await adjustUserBalance(
        selectedUser.user_id,
        deltaBalls.trim(),
        reason.trim(),
      );
      setSelectedUser({
        ...selectedUser,
        balance_balls: response.balance_balls,
      });
      setDeltaBalls("");
      setReason("");
      setTransactionsPage(0);
      await loadTransactions(selectedUser.user_id, 0, transactionsPageSize);
      setToast({ message: "Баланс пользователя изменён", severity: "success" });
    } catch (error) {
      console.error("Failed to adjust user balance", error);
      setToast({
        message: "Не удалось изменить баланс пользователя",
        severity: "error",
      });
    } finally {
      setSavingAdjustment(false);
      setDeductionConfirmationOpen(false);
    }
  };

  const submitAdjustment = async () => {
    const amount = validateAdjustment();
    if (amount === null) return;
    if (amount < 0) {
      setDeductionConfirmationOpen(true);
      return;
    }
    await performAdjustment();
  };

  const changeTransactionsPage = async (_event: unknown, page: number) => {
    if (!selectedUser) return;
    setTransactionsPage(page);
    setLoadingUser(true);
    try {
      await loadTransactions(selectedUser.user_id, page, transactionsPageSize);
    } catch (error) {
      console.error("Failed to load balance transactions", error);
      setToast({
        message: "Не удалось загрузить транзакции",
        severity: "error",
      });
    } finally {
      setLoadingUser(false);
    }
  };

  const changeTransactionsPageSize = async (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (!selectedUser) return;
    const pageSize = Number(event.target.value);
    setTransactionsPageSize(pageSize);
    setTransactionsPage(0);
    setLoadingUser(true);
    try {
      await loadTransactions(selectedUser.user_id, 0, pageSize);
    } catch (error) {
      console.error("Failed to change transaction page size", error);
      setToast({
        message: "Не удалось загрузить транзакции",
        severity: "error",
      });
    } finally {
      setLoadingUser(false);
    }
  };

  return (
    <>
      <EdgeDataTable
        ariaLabel="Список пользователей"
        columns={columns}
        rows={users}
        getCell={getCell}
        rowKey={(row) => row.user_id}
        loading={loadingUsers}
        emptyMessage="Пользователи не найдены"
        onRowClick={openUser}
        server={{
          total: totalUsers,
          page: usersPage,
          pageSize: usersPageSize,
          pageSizeOptions: [10, 25, 50, 100],
          onPageChange: setUsersPage,
          onPageSizeChange: (pageSize) => {
            setUsersPageSize(pageSize);
            setUsersPage(0);
          },
        }}
        renderMobileCard={(user) => (
          <>
            <Typography variant="h6">
              {user.name || user.email || `Пользователь ${user.user_id}`}
            </Typography>
            <Typography color="text.secondary">
              {user.email || "Email не указан"}
            </Typography>
            <Typography variant="body2">ID: {user.user_id}</Typography>
            <Typography variant="body2">
              {formatDate(user.created_at)}
            </Typography>
          </>
        )}
        bottomTiles={
          <BarTile>
            <Box
              component="form"
              onSubmit={handleSearch}
              aria-label="Поиск пользователей"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <TextField
                size="small"
                placeholder="Email, имя или идентификатор"
                value={searchInput}
                inputProps={{ "aria-label": "Email, имя или идентификатор" }}
                onChange={(event) => setSearchInput(event.target.value)}
              />
              <Button type="submit" size="small" variant="contained">
                Найти
              </Button>
            </Box>
          </BarTile>
        }
      />

      <Dialog
        open={userDialogOpen}
        onClose={() => setUserDialogOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          {selectedUser?.name || selectedUser?.email || "Пользователь"}
          {selectedUser && ` · ID ${selectedUser.user_id}`}
        </DialogTitle>
        <DialogContent dividers>
          {loadingUser && !selectedUser ? (
            <CenteredState>
              <CircularProgress />
            </CenteredState>
          ) : selectedUser ? (
            <Stack spacing={3}>
              <Box>
                <Typography color="text.secondary">
                  {selectedUser.email || "Email не указан"}
                </Typography>
                <Typography variant="h5">
                  Баланс: {formatBallValue(selectedUser.balance_balls)}
                </Typography>
              </Box>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Корректировка баланса
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Положительная сумма пополняет баланс, отрицательная —
                  списывает.
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Сумма, баллы"
                    type="number"
                    value={deltaBalls}
                    onChange={(event) => setDeltaBalls(event.target.value)}
                    inputProps={{ step: "any" }}
                  />
                  <TextField
                    label="Комментарий"
                    required
                    fullWidth
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    inputProps={{ maxLength: 500 }}
                  />
                  <Button
                    variant="contained"
                    disabled={savingAdjustment}
                    onClick={submitAdjustment}
                  >
                    {savingAdjustment ? "Сохранение…" : "Применить"}
                  </Button>
                </Stack>
              </Paper>

              <Divider />
              <Typography variant="h6">Транзакции</Typography>
              <TableContainer component={Paper} variant="outlined">
                <EdgeTable size="small" aria-label="Транзакции пользователя">
                  <TableHead>
                    <TableRow>
                      <TableCell>Дата</TableCell>
                      <TableCell>Тип</TableCell>
                      <TableCell align="right">Изменение</TableCell>
                      <TableCell>Комментарий</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          {formatDate(transaction.created_at)}
                        </TableCell>
                        <TableCell>
                          {TRANSACTION_LABELS[transaction.kind]}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color:
                              Number(transaction.delta_balls) < 0
                                ? "error.main"
                                : "success.main",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {Number(transaction.delta_balls) > 0 ? "+" : ""}
                          {formatBallValue(transaction.delta_balls)}
                        </TableCell>
                        <TableCell>{transactionComment(transaction)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </EdgeTable>
              </TableContainer>
              {transactions.length === 0 && (
                <Typography color="text.secondary">
                  Транзакций пока нет
                </Typography>
              )}
              <TablePagination
                component="div"
                count={totalTransactions}
                page={transactionsPage}
                rowsPerPage={transactionsPageSize}
                rowsPerPageOptions={[10, 25, 50, 100]}
                onPageChange={changeTransactionsPage}
                onRowsPerPageChange={changeTransactionsPageSize}
                labelRowsPerPage="На странице"
              />
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deductionConfirmationOpen}
        onClose={() => setDeductionConfirmationOpen(false)}
      >
        <DialogTitle>Подтвердите списание</DialogTitle>
        <DialogContent>
          <Typography>
            Списать {formatBallValue(deltaBalls)} у пользователя{" "}
            {selectedUser?.email || selectedUser?.user_id}?
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Причина: {reason}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeductionConfirmationOpen(false)}>
            Отмена
          </Button>
          <Button color="error" variant="contained" onClick={performAdjustment}>
            Списать
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
      >
        <Alert
          severity={toast?.severity || "success"}
          onClose={() => setToast(null)}
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AdminUsers;
